import { forwardRef } from 'react';

import { useEndToEndTestId } from '@perpay-web/hooks/useEndToEndTestId';
import { getClassName } from '@perpay-web/utils/domUtils';
import { noop } from '@perpay-web/utils/noop';

import styles from './CustomButton.scss';

/**
 * @typedef {Object} CustomButtonProps
 * @property {('primary'|'secondary'|'tertiary'|'link'|'inline-link'|'attention')} [action='primary'] - The button's action type
 * @property {('small'|'medium'|'large'|'')} [size=''] - The button's size
 * @property {boolean} [disabled=false] - Whether the button is disabled
 * @property {React.MouseEventHandler<HTMLButtonElement>} [onClick=noop] - Click handler
 * @property {('button'|'submit'|'reset')} [type='button'] - The button's type
 * @property {React.ReactNode} [children=null] - Button content
 * @property {boolean} [loading=false] - Whether the button is in loading state
 * @property {string} [className=''] - Additional CSS class name
 * @property {string} [aria-label] - ARIA label for accessibility
 */

/**
 * @type {React.ForwardRefExoticComponent<CustomButtonProps & React.RefAttributes<HTMLButtonElement | HTMLAnchorElement>>}
 */
const CustomButton = forwardRef(
    (
        {
            action = 'primary',
            size = '',
            disabled = false,
            onClick = noop,
            type = 'button',
            children = null,
            loading = false,
            className = '',
            'aria-label': ariaLabel,
        },
        ref,
    ) => {
        const isInlineLink = action === 'inline-link';
        const TagName = isInlineLink ? 'a' : 'button';
        const buttonStyles = !isInlineLink
            ? getClassName(
                  styles.button,
                  styles[`button--${action}`],
                  styles[`button--${size}`],
                  loading ? styles['button--loading'] : '',
              )
            : '';
        const endToEndTestIDParams = useEndToEndTestId();

        return (
            <TagName
                ref={ref}
                className={getClassName(buttonStyles, className)}
                disabled={disabled || loading}
                onClick={(e) => {
                    if (isInlineLink) {
                        e.preventDefault();
                    }
                    onClick(e);
                }}
                type={isInlineLink ? null : type}
                role={isInlineLink ? 'button' : null}
                href={isInlineLink ? '#' : null}
                aria-label={ariaLabel}
                {...endToEndTestIDParams}
            >
                {loading && !isInlineLink ? (
                    <div className={styles.loader} />
                ) : (
                    children
                )}
            </TagName>
        );
    },
);

/**
 * @type {any}
 */
export default CustomButton;
